import React, { useState, useEffect, useRef, useCallback } from 'react';
import useKioskManifest from '../hooks/useKioskManifest';

const API = process.env.REACT_APP_URL;
const KIOSK_AUTH_KEY = 'kiosk_auth'; // { token, username, expiresAt }

/* ── Geo helpers ─────────────────────────────────────────────────────────── */
function getGeo() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('GPS not supported'));
    navigator.geolocation.getCurrentPosition(
      pos => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      err => reject(err.code === 1
        ? new Error('Location access denied. Tap the lock icon in your address bar, set Location to "Allow", then reload.')
        : new Error('Unable to get location. Enable GPS and try again.')),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  });
}

/* ── Check saved kiosk auth ──────────────────────────────────────────────── */
function getSavedAuth() {
  try {
    const raw = localStorage.getItem(KIOSK_AUTH_KEY);
    if (!raw) return null;
    const auth = JSON.parse(raw);
    if (auth.expiresAt && Date.now() < auth.expiresAt) return auth;
    localStorage.removeItem(KIOSK_AUTH_KEY);
    return null;
  } catch (e) { return null; }
}

/* ── Main component ─────────────────────────────────────────────────────── */
export default function KioskRegister() {
  useKioskManifest('/manifest-register.json', '#0c4a6e');
  // Kiosk auth state
  const [kioskAuth, setKioskAuth] = useState(() => getSavedAuth());
  const [authUser, setAuthUser]       = useState('');
  const [authOtp, setAuthOtp]         = useState('');
  const [authStep, setAuthStep]       = useState('username'); // 'username' | 'otp' | 'password'
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError]     = useState('');
  const [authResend, setAuthResend]   = useState(0);
  const [authPassword, setAuthPassword] = useState('');
  const authTimerRef = useRef(null);
  const otpRef = useRef(null);
  const pwRef = useRef(null);

  // Geo state
  const [geoState, setGeoState] = useState('checking'); // 'checking' | 'ok' | 'denied' | 'error'
  const [geoError, setGeoError] = useState('');
  const [coords, setCoords] = useState(null);

  // Branch config (saved in localStorage)
  const [branch, setBranch] = useState(() => localStorage.getItem('kiosk_branch') || 'MedicalYogaTherapyCentre');
  const [showSettings, setShowSettings] = useState(false);
  const [branches, setBranches] = useState([]);

  // Form
  const emptyForm = { name: '', number: '', age: '', gender: 'male', bg: '', occu: '', address: '', now: {} };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { patientCard, name }
  const [error, setError] = useState('');

  // Add-to-home-screen
  const [showInstall, setShowInstall] = useState(false);
  const deferredPrompt = useRef(null);

  // ── Dynamic favicon ──
  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 64; canvas.height = 64;
    const ctx = canvas.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 64, 64);
    g.addColorStop(0, '#059669'); g.addColorStop(1, '#10b981');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(32, 32, 32, 0, Math.PI * 2); ctx.fill();
    // Plus / person icon
    ctx.strokeStyle = '#fff'; ctx.lineWidth = 5; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.arc(32, 22, 8, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(20, 42); ctx.quadraticCurveTo(32, 32, 44, 42); ctx.stroke();
    ctx.font = 'bold 11px Arial'; ctx.fillStyle = '#fff'; ctx.textAlign = 'center';
    ctx.fillText('MYC', 32, 60);
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.rel = 'icon'; link.href = canvas.toDataURL();
    document.head.appendChild(link);
    document.title = 'MYC — Saadhak Registration';
  }, []);

  // ── Geo check on mount ──
  useEffect(() => {
    (async () => {
      try {
        const c = await getGeo();
        setCoords(c);
        setGeoState('ok');
      } catch (err) {
        setGeoState(err.message.includes('denied') ? 'denied' : 'error');
        setGeoError(err.message);
      }
    })();
  }, []);

  // ── Kiosk OTP auth handlers ──
  function startAuthResend() {
    setAuthResend(60);
    clearInterval(authTimerRef.current);
    authTimerRef.current = setInterval(() => {
      setAuthResend(t => { if (t <= 1) { clearInterval(authTimerRef.current); return 0; } return t - 1; });
    }, 1000);
  }

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!authUser.trim()) return setAuthError('Please enter your username.');
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API}/kiosk-otp/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: authUser.trim(), userLat: coords?.lat, userLon: coords?.lon }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send OTP');
      if (data.mode === 'password') {
        setAuthStep('password');
      } else {
        setAuthStep('otp');
        startAuthResend();
        if (data.devOtp) setAuthError(`[DEV] OTP: ${data.devOtp}`);
      }
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const isPassword = authStep === 'password';
    if (!isPassword && (!authOtp || authOtp.length !== 4)) return setAuthError('Enter the 4-digit OTP.');
    if (isPassword && !authPassword) return setAuthError('Enter your password.');
    setAuthLoading(true);
    setAuthError('');
    try {
      const body = { username: authUser.trim() };
      if (isPassword) body.password = authPassword;
      else body.otp = authOtp.trim();
      const res = await fetch(`${API}/kiosk-otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || (isPassword ? 'Invalid password' : 'Invalid OTP'));
      const kioskDays = data.data.kioskSessionDays || 5;
      const authObj = { token: data.data.token, username: data.data.username, expiresAt: Date.now() + kioskDays * 24 * 60 * 60 * 1000 };
      localStorage.setItem(KIOSK_AUTH_KEY, JSON.stringify(authObj));
      setKioskAuth(authObj);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    if (authStep === 'otp' && otpRef.current) otpRef.current.focus();
    if (authStep === 'password' && pwRef.current) pwRef.current.focus();
  }, [authStep]);

  // ── Fetch branches for settings ──
  useEffect(() => {
    fetch(`${API}/patients/public/branches`)
      .then(r => r.json())
      .then(data => {
        setBranches(data || []);
        // Auto-select first branch if none saved
        if (!branch && data?.length > 0) {
          const first = data[0].name;
          setBranch(first);
          localStorage.setItem('kiosk_branch', first);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Install prompt (Android) ──
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); deferredPrompt.current = e; };
    window.addEventListener('beforeinstallprompt', handler);
    // iOS: show manual instructions once
    if (!localStorage.getItem('kiosk_shortcut_prompted')) {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS || !('beforeinstallprompt' in window)) {
        setShowInstall(true);
        localStorage.setItem('kiosk_shortcut_prompted', '1');
      }
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
    }
    setShowInstall(false);
    localStorage.setItem('kiosk_shortcut_prompted', '1');
  };

  // ── Form helpers ──
  const set = useCallback((field) => (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, [field]: val }));
    setError('');
  }, []);

  const toggleNow = useCallback((k) => {
    setForm(f => ({ ...f, now: { ...f.now, [k]: !f.now[k] } }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Please enter saadhak name.');
    if (!form.number.trim() || form.number.replace(/\D/g, '').length !== 10) return setError('Please enter a valid 10-digit phone number.');
    if (!branch) return setError('Please select a branch.');

    setSubmitting(true);
    setError('');
    try {
      const hdrs = { 'Content-Type': 'application/json' };
      if (kioskAuth?.token) {
        hdrs['Authorization'] = `Bearer ${kioskAuth.token}`;
        if (kioskAuth.username) hdrs['x-username'] = kioskAuth.username.toLowerCase();
      }
      const res = await fetch(`${API}/patients/public/register`, {
        method: 'POST',
        headers: hdrs,
        body: JSON.stringify({
          ...form,
          number: form.number.replace(/\D/g, ''),
          age: form.age ? Number(form.age) : undefined,
          branch,
          userLat: coords?.lat,
          userLon: coords?.lon,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Registration failed');
      setSuccess(data);
      // Auto-reset after 8 seconds
      setTimeout(() => { setSuccess(null); setForm(emptyForm); }, 8000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Styles ──
  const pageStyle = { minHeight: '100vh', background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfeff 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 16px 40px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' };
  const cardStyle = { background: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,.08)', width: '100%', maxWidth: 480, overflow: 'hidden' };
  const headerStyle = { background: 'linear-gradient(135deg, #059669, #10b981)', padding: '24px 24px 20px', color: '#fff', textAlign: 'center' };
  const inputStyle = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 15, background: '#fafafa', boxSizing: 'border-box', color: '#1e293b' };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.3px', marginBottom: 4, display: 'block' };

  // ── Kiosk OTP auth gate (shown after geo passes) ──
  if (geoState === 'ok' && !kioskAuth) {
    const authInputStyle = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1.5px solid #d1d5db', fontSize: 16, background: '#fafafa', boxSizing: 'border-box', color: '#1e293b', textAlign: 'center', letterSpacing: 1 };
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, maxWidth: 420 }}>
          <div style={headerStyle}>
            <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 4, marginBottom: 4 }}>MYC</div>
            <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase' }}>Staff Authentication</div>
          </div>
          <div style={{ padding: '24px 28px 32px' }}>
            {authStep === 'username' && (
              <form onSubmit={handleRequestOtp}>
                <div style={{ fontSize: 14, color: '#475569', marginBottom: 16, textAlign: 'center', lineHeight: 1.5 }}>
                  Enter your staff username to continue.
                </div>
                <input type="text" value={authUser} onChange={e => { setAuthUser(e.target.value); setAuthError(''); }}
                  placeholder="Username" style={authInputStyle} autoComplete="off" autoFocus />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#059669' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : 'linear-gradient(135deg, #059669, #10b981)',
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Checking...' : 'Continue'}
                </button>
              </form>
            )}
            {authStep === 'otp' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthOtp(''); setAuthError(''); }}
                    style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>
                  OTP sent to your registered WhatsApp number.
                </div>
                <input ref={otpRef} type="number" value={authOtp} onChange={e => { setAuthOtp(e.target.value.slice(0, 4)); setAuthError(''); }}
                  placeholder="4-digit OTP" style={authInputStyle} inputMode="numeric" />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: authError.startsWith('[DEV]') ? '#059669' : '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 10, textAlign: 'center' }}>
                  {authResend > 0 ? `Resend in ${authResend}s` : (
                    <button type="button" onClick={(e) => { handleRequestOtp(e); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#059669', fontSize: 12, textDecoration: 'underline' }}>Resend OTP</button>
                  )}
                </div>
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : 'linear-gradient(135deg, #059669, #10b981)',
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </form>
            )}
            {authStep === 'password' && (
              <form onSubmit={handleVerify}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#475569' }}>User: <strong>{authUser}</strong></span>
                  <button type="button" onClick={() => { setAuthStep('username'); setAuthPassword(''); setAuthError(''); }}
                    style={{ fontSize: 12, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14, textAlign: 'center' }}>
                  Enter your password to continue.
                </div>
                <input ref={pwRef} type="password" value={authPassword} onChange={e => { setAuthPassword(e.target.value); setAuthError(''); }}
                  placeholder="Password" style={{ ...authInputStyle, letterSpacing: 3 }} />
                {authError && <div style={{ marginTop: 10, fontSize: 13, color: '#dc2626', textAlign: 'center' }}>{authError}</div>}
                <button type="submit" disabled={authLoading} style={{
                  width: '100%', marginTop: 16, padding: '13px', borderRadius: 10, border: 'none',
                  background: authLoading ? '#94a3b8' : 'linear-gradient(135deg, #059669, #10b981)',
                  color: '#fff', fontSize: 15, fontWeight: 700, cursor: authLoading ? 'not-allowed' : 'pointer',
                }}>
                  {authLoading ? 'Verifying...' : 'Login & Continue'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Geo blocked ──
  if (geoState === 'checking') {
    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📍</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Checking location...</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Please allow location access when prompted.</div>
        </div>
      </div>
    );
  }

  if (geoState === 'denied' || geoState === 'error') {
    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={{ ...cardStyle, maxWidth: 400, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>Location Required</h2>
          <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{geoError || 'This form is only available at the MYC centre. Please enable GPS.'}</p>
          {geoState === 'denied' && (
            <div style={{ background: '#fef3c7', borderRadius: 10, padding: '12px 16px', marginTop: 16, fontSize: 13, color: '#92400e', textAlign: 'left', lineHeight: 1.6 }}>
              <strong>To enable location:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                <li>Tap the lock/info icon in the address bar</li>
                <li>Set Location to "Allow"</li>
                <li>Reload this page</li>
              </ul>
            </div>
          )}
          <button onClick={() => window.location.reload()} style={{ marginTop: 20, padding: '10px 24px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Success screen ──
  if (success) {
    return (
      <div style={{ ...pageStyle, justifyContent: 'center' }}>
        <div style={{ ...cardStyle, maxWidth: 420, textAlign: 'center', overflow: 'hidden' }}>
          {/* Green header band */}
          <div style={{ background: 'linear-gradient(135deg, #059669, #10b981)', padding: '32px 24px 24px', color: '#fff' }}>
            <div style={{ fontSize: 56, marginBottom: 8, lineHeight: 1 }}>🙏</div>
            <h2 style={{ fontSize: 24, fontWeight: 900, margin: '0 0 6px', letterSpacing: 0.5 }}>
              Namaste {success.name} Ji
            </h2>
            <p style={{ fontSize: 14, opacity: 0.9, margin: 0 }}>
              You have been successfully enrolled!
            </p>
          </div>

          {/* Card body */}
          <div style={{ padding: '24px 28px 32px' }}>
            <div style={{ background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)', border: '2px solid #86efac', borderRadius: 14, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#059669', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Your Health Card ID</div>
              <div style={{ fontSize: 32, fontWeight: 900, color: '#065f46', marginTop: 6, letterSpacing: 2, fontFamily: 'monospace' }}>{success.patientCard}</div>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Please save this number for future reference</div>
            </div>

            {success.messageSent && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 16, fontSize: 13, color: '#059669' }}>
                <span>✓</span> Welcome message sent to your phone
              </div>
            )}

            <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>This screen will reset automatically...</p>
            <button onClick={() => { setSuccess(null); setForm(emptyForm); }} style={{
              padding: '10px 24px', borderRadius: 8, border: '1.5px solid #d1d5db',
              background: '#fff', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              Register Another Saadhak
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ──
  return (
    <div style={pageStyle}>
      {/* Install prompt */}
      {showInstall && (
        <div style={{ width: '100%', maxWidth: 480, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, flexShrink: 0,
            background: 'linear-gradient(135deg, #059669, #10b981)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
          }}>
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 900, letterSpacing: 1 }}>MYC</span>
          </div>
          <div style={{ flex: 1, fontSize: 13, color: '#1e40af', lineHeight: 1.5 }}>
            <strong>Quick Access:</strong> Tap <strong>Share → Add to Home Screen</strong> to save this as a shortcut.
          </div>
          <button onClick={handleInstall} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {deferredPrompt.current ? 'Install' : 'Got it'}
          </button>
        </div>
      )}

      <div style={cardStyle}>
        {/* Header */}
        <div style={{ ...headerStyle, position: 'relative' }}>
          <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 4, marginBottom: 4 }}>MYC</div>
          <div style={{ fontSize: 11, letterSpacing: 2, opacity: 0.8, textTransform: 'uppercase' }}>Medical Yoga Therapy Centre</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '14px 0 0', opacity: 0.95 }}>New Saadhak Registration</h1>
          {kioskAuth && <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>Staff: {kioskAuth.username}</div>}
          {/* Settings gear */}
          <button onClick={() => setShowSettings(s => !s)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16 }}>
            ⚙️
          </button>
          {kioskAuth && (
            <button onClick={() => { localStorage.removeItem(KIOSK_AUTH_KEY); setKioskAuth(null); setAuthStep('username'); setAuthUser(''); setAuthOtp(''); }}
              style={{ position: 'absolute', top: 16, left: 16, background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', fontSize: 11, color: '#fff', fontWeight: 600 }}>
              Logout
            </button>
          )}
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div style={{ padding: '14px 24px', background: '#fefce8', borderBottom: '1px solid #fde68a' }}>
            <label style={{ ...labelStyle, color: '#92400e' }}>Centre Branch</label>
            <select value={branch} onChange={e => { setBranch(e.target.value); localStorage.setItem('kiosk_branch', e.target.value); }} style={{ ...inputStyle, background: '#fff' }}>
              <option value="">Select Branch</option>
              {branches.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
            </select>
            <div style={{ fontSize: 11, color: '#a16207', marginTop: 4 }}>This setting is saved on this device.</div>
          </div>
        )}

        {/* No branch warning — only show if branch not set and settings hidden */}

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '20px 24px 28px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 14px' }}>
            {/* Branch — full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Branch *</label>
              <select value={branch} onChange={e => { setBranch(e.target.value); localStorage.setItem('kiosk_branch', e.target.value); }} style={inputStyle}>
                <option value="">Select Branch</option>
                {branches.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
              </select>
            </div>
            {/* Name — full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Full Name *</label>
              <input type="text" value={form.name} onChange={set('name')} style={inputStyle} placeholder="Saadhak's full name" autoComplete="off" />
            </div>

            {/* Phone */}
            <div>
              <label style={labelStyle}>Phone Number *</label>
              <input type="tel" value={form.number} onChange={e => { setForm(f => ({ ...f, number: e.target.value.replace(/\D/g, '').slice(0, 10) })); setError(''); }} style={inputStyle} placeholder="10-digit number" maxLength={10} inputMode="numeric" autoComplete="off" />
            </div>

            {/* Age */}
            <div>
              <label style={labelStyle}>Age</label>
              <input type="text" value={form.age} onChange={set('age')} style={inputStyle} placeholder="e.g. 45" maxLength={3} inputMode="numeric" />
            </div>

            {/* Gender */}
            <div>
              <label style={labelStyle}>Gender</label>
              <select value={form.gender} onChange={set('gender')} style={inputStyle}>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="noanswer">Prefer Not to Answer</option>
              </select>
            </div>

            {/* Blood Group */}
            <div>
              <label style={labelStyle}>Blood Group</label>
              <select value={form.bg} onChange={set('bg')} style={inputStyle}>
                <option value="">Select</option>
                {['O-','O+','A-','A+','B-','B+','AB-','AB+'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
              </select>
            </div>

            {/* Occupation */}
            <div>
              <label style={labelStyle}>Occupation</label>
              <input type="text" value={form.occu} onChange={set('occu')} style={inputStyle} placeholder="e.g. Teacher" autoComplete="off" />
            </div>

            {/* Address — full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Address</label>
              <input type="text" value={form.address} onChange={set('address')} style={inputStyle} placeholder="Full address" autoComplete="off" />
            </div>

            {/* Nature of Work — full width */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>Nature of Work</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {['sit', 'stand', 'travel', 'mix', 'other'].map(k => (
                  <button type="button" key={k} onClick={() => toggleNow(k)} style={{
                    padding: '7px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer',
                    border: `1.5px solid ${form.now[k] ? '#059669' : '#d1d5db'}`,
                    background: form.now[k] ? '#ecfdf5' : '#fafafa',
                    color: form.now[k] ? '#065f46' : '#374151',
                    fontWeight: form.now[k] ? 600 : 400,
                  }}>
                    {k.charAt(0).toUpperCase() + k.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 13, color: '#dc2626' }}>
              {error}
            </div>
          )}

          {/* Submit */}
          <button type="submit" disabled={submitting} style={{
            width: '100%', marginTop: 20, padding: '14px', borderRadius: 10, border: 'none',
            background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #059669, #10b981)',
            color: '#fff', fontSize: 16, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
            boxShadow: '0 2px 8px rgba(5,150,105,.25)',
          }}>
            {submitting ? 'Registering...' : 'Register Saadhak'}
          </button>
        </form>
      </div>

      {/* Footer */}
      <div style={{ marginTop: 20, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
        MYC Saadhak Registration Kiosk
      </div>
    </div>
  );
}
